/**
 * 「AIをつなぐ」の絵（2026-10-19 design/visual-guides・founder「文字ばっかりだな。図解とかスクショを使おうよ」「実画面じゃなくても、コードで画面を書いてもいい」）。
 * ホームの「つなぐ」の段（HomeC.tsx）と使い方ガイドの始め方 1（GuidePage.tsx）で、貼る文とコピーの下に置く。
 *
 * 形: 上に AI の名前の切り替え（ChatGPT・Claude・Claude Code・Codex・Cursor）、下に 4 段の小さな画面の絵と 1 行ずつの言葉。
 *   どの AI で何の段かは connectPictures.ts（純関数・テストあり）。ここは描くだけ。
 * WHY 本物の画面写真ではなく HTML / CSS の絵か（サイトの src/screens.mjs と同じ考え）:
 *   - ほかの会社の画面の写し・ロゴは使わない（決まり）。押す所の名前が変わっても文字を直すだけ
 *   - ダッシュボードの色の変数で描く = 暗い表示にそのまま付いてくる・画像の読み込みが無い
 *   - 絵の中の文字は本物の文字 = 拡大しても潰れない。絵ごとに role="img" + aria-label（押す所を言葉で）
 * 押す所・打つ所は 1 枚に 1 つだけ（.cp-hot = 橙の輪。AI が自分でやる段 = connect のコマンドには付けない。フォーカスの輪と同じ色 = 「ここ」の印）。絵の中の言葉は英語のまま（相手の画面の言葉 = サイトの手順と同じ）。
 * 主のボタンは作らない（この段の主はコピー = 1 画面に 1 つ）。切り替えは静かな札。
 */
import { useState, type ReactNode } from 'react';
import { CLI } from '../cliCommand';
import { CONNECT_AIS, connectAiInfo, readConnectAi, saveConnectAi, type ConnectAi, type ConnectAiInfo, type PicStep } from '../connectPictures';
import { useT, type MessageKey } from '../i18n';
import { deviceStorage } from '../lastLogin';
import { PRODUCT_NAME } from '../productName';

export function ConnectPictures() {
  const { t } = useT();
  const [ai, setAi] = useState<ConnectAi>(() => readConnectAi(deviceStorage()));
  const info = connectAiInfo(ai);
  const vars = { ai: info.name, product: PRODUCT_NAME };
  return (
    <div className="cp" role="group" aria-label={t('home.step.connect.pics.label')}>
      <div className="cp-head">
        <p className="cp-label">{t('home.step.connect.pics.label')}</p>
        <div className="cp-tabs">
          {CONNECT_AIS.map((a) => (
            <button
              key={a.id}
              type="button"
              className={`cp-tab ${a.id === ai ? 'is-on' : ''}`}
              aria-pressed={a.id === ai}
              onClick={() => {
                setAi(a.id);
                saveConnectAi(deviceStorage(), a.id);
              }}
            >
              {a.name}
            </button>
          ))}
        </div>
      </div>
      <ol className="cp-steps">
        {info.steps.map((s, i) => (
          <li key={`${ai}-${s}`} className="cp-step">
            <span className="cp-art" role="img" aria-label={t(`home.step.connect.pics.alt.${s}` as MessageKey, vars)}>
              <Picture step={s} info={info} />
            </span>
            <span className="cp-cap">
              <span className="cp-n" aria-hidden="true">
                {i + 1}
              </span>
              {t(`home.step.connect.pics.${s}` as MessageKey, vars)}
            </span>
          </li>
        ))}
      </ol>
      <p className="cp-note">{t('home.step.connect.pics.note')}</p>
    </div>
  );
}

// ---------------------------------------------------------------- 絵の部品（どれも飾りの span。読み上げは外の aria-label）

const Hot = ({ children, className = '' }: { children: ReactNode; className?: string }) => <span className={`cp-hot ${className}`}>{children}</span>;
/** 薄い横棒 = 文字の行の代わり（読ませない所は字を書かない = 押す所の言葉だけが目に入る） */
const Lines = ({ n = 2, short = false }: { n?: number; short?: boolean }) => (
  <>
    {Array.from({ length: n }, (_, i) => (
      <span key={i} className={`cp-line ${short && i === n - 1 ? 'is-short' : ''}`} />
    ))}
  </>
);

/** 窓。web = アドレスの欄・terminal = 暗い端末・editor = 左にコード、右に会話の欄 */
function Win({ info, where, children }: { info: ConnectAiInfo; where?: string; children: ReactNode }) {
  return (
    <span className={`cp-win is-${info.frame}`}>
      <span className="cp-bar">
        <i />
        <i />
        <i />
        <span className="cp-where">{where ?? info.where}</span>
      </span>
      <span className="cp-body">{children}</span>
    </span>
  );
}

/** 会話の入力欄。typed = 打った文字 */
function Composer({ typed, send = false }: { typed: ReactNode; send?: boolean }) {
  return (
    <span className="cp-composer">
      <span className="cp-typed">{typed}</span>
      {send ? (
        <Hot className="cp-send">↑</Hot>
      ) : (
        <span className="cp-send" aria-hidden="true">
          ↑
        </span>
      )}
    </span>
  );
}

/** KUMODeck の許可のページ（apps/mcp/src/auth/pages.ts の consentPage と同じ言葉）。パソコンの AI でもブラウザで開く */
function AllowPage({ info }: { info: ConnectAiInfo }) {
  return (
    <Win info={{ ...info, frame: 'web' }} where={PRODUCT_NAME}>
      <span className="cp-page">
        <b className="cp-h">
          Allow {info.name} to use {PRODUCT_NAME}?
        </b>
        <Lines n={1} short />
        <span className="cp-actions">
          <span className="cp-btn">Cancel</span>
          <Hot className="cp-btn is-primary">Allow</Hot>
        </span>
      </span>
    </Win>
  );
}

const PASTED = `Connect ${PRODUCT_NAME} to yourself…`;

function Picture({ step, info }: { step: PicStep; info: ConnectAiInfo }) {
  switch (step) {
    case 'paste':
      if (info.frame === 'terminal')
        return (
          <Win info={info}>
            <span className="cp-term">
              <Lines n={1} short />
              <Hot className="cp-prompt">&gt; {PASTED}</Hot>
            </span>
          </Win>
        );
      return (
        <Win info={info}>
          {info.frame === 'editor' ? (
            <span className="cp-editor">
              <span className="cp-code">
                <Lines n={4} short />
              </span>
              <span className="cp-side">
                <Composer typed={PASTED} send />
              </span>
            </span>
          ) : (
            <span className="cp-convo">
              <Lines n={2} short />
              <Composer typed={PASTED} send />
            </span>
          )}
        </Win>
      );
    case 'chatgptAdd':
    case 'claudeAdd': {
      const chatgpt = step === 'chatgptAdd';
      return (
        <Win info={info} where={chatgpt ? 'chatgpt.com/plugins' : 'claude.ai/customize'}>
          <span className="cp-settings">
            <span className="cp-nav">
              <span className="is-on" />
              <span />
              <span />
            </span>
            <span className="cp-page">
              <b className="cp-h">{chatgpt ? 'Plugins' : 'Connectors'}</b>
              <span className="cp-btn is-small">{chatgpt ? '+' : '+ Add'}</span>
              <span className="cp-menu">
                <Hot className="cp-item is-wrap">{chatgpt ? 'Add custom MCP server' : 'Add custom connector'}</Hot>
              </span>
            </span>
          </span>
        </Win>
      );
    }
    case 'allow':
      return <AllowPage info={info} />;
    case 'chatgptUse':
      return (
        <Win info={info}>
          <span className="cp-convo">
            <span className="cp-menu">
              <Hot className="cp-item">{PRODUCT_NAME}</Hot>
            </span>
            <Composer typed={<b>@</b>} />
          </span>
        </Win>
      );
    case 'claudeUse':
      return (
        <Win info={info}>
          <span className="cp-convo">
            <span className="cp-menu">
              <span className="cp-item">Connectors ›</span>
              <Hot className="cp-item cp-toggle">
                {PRODUCT_NAME}
                <i />
              </Hot>
            </span>
            <Composer typed={<b>+</b>} />
          </span>
        </Win>
      );
    case 'runs':
      if (info.frame === 'editor')
        return (
          <Win info={info}>
            <span className="cp-editor">
              <span className="cp-code">
                <Lines n={4} short />
              </span>
              <span className="cp-side">
                <span className="cp-run">
                  <code>{CLI} connect</code>
                </span>
                <span className="cp-ok">✓ Connected</span>
              </span>
            </span>
          </Win>
        );
      return (
        <Win info={info}>
          <span className="cp-term">
            <code>$ {CLI} connect</code>
            <Lines n={1} short />
            <span className="cp-ok">✓ Connected</span>
          </span>
        </Win>
      );
    case 'ask':
      // 頼む段: AI に頼みを打つ（印）・右上にこのダッシュボードの段の「Connected」の札（画面が自分で進む = HomeC の Waiting の続き。押す物ではないので印なし）
      return (
        <span className="cp-ask">
          <Win info={info}>
            {info.frame === 'terminal' ? (
              <span className="cp-term">
                <Hot className="cp-prompt">&gt; Make a small game…</Hot>
              </span>
            ) : info.frame === 'editor' ? (
              <span className="cp-editor">
                <span className="cp-code">
                  <Lines n={4} short />
                </span>
                <span className="cp-side">
                  <Composer typed="Make a small game…" send />
                </span>
              </span>
            ) : (
              <span className="cp-convo">
                <Lines n={1} short />
                <Composer typed="Make a small game…" send />
              </span>
            )}
          </Win>
          <span className="cp-done">✓ Connected</span>
        </span>
      );
  }
}
